/* Keyboard access for editor-authored RTE tables. Enhance the actual overflow
   element, retaining native table semantics, keys and merchant attributes.
   Without JS the original table and pointer scrolling remain available. */
(() => {
  if (window.__jpRteTablesInit) return;
  window.__jpRteTablesInit = true;

  const selector = '.rte, .jp-page-body';
  const controllers = new Map();

  const create = (root) => {
    const owned = new Set();
    const marked = new Set();
    const observed = new Set();
    let frame = 0;
    let destroyed = false;
    const schedule = () => {
      if (!frame && !destroyed) frame = requestAnimationFrame(update);
    };
    const resize = window.ResizeObserver ? new ResizeObserver(schedule) : null;
    const mutations = new MutationObserver(schedule);

    const forget = (element) => {
      // If another controller/merchant changed the value, that value wins.
      if (element.getAttribute('tabindex') === '0') element.removeAttribute('tabindex');
      owned.delete(element);
    };
    const unmark = (element) => {
      if (element.getAttribute('data-jp-rte-scroll') === '') element.removeAttribute('data-jp-rte-scroll');
      marked.delete(element);
    };
    const destroy = () => {
      destroyed = true;
      cancelAnimationFrame(frame);
      mutations.disconnect();
      resize?.disconnect();
      root.removeEventListener('focusout', schedule);
      root.removeEventListener('load', schedule, true);
      owned.forEach(forget);
      marked.forEach(unmark);
      controllers.delete(root);
    };
    function update() {
      frame = 0;
      if (!root.isConnected) { destroy(); return; }
      const overflow = new Set();
      const targets = new Set([root]);
      root.querySelectorAll('table').forEach((table) => {
        // A block table may scroll itself; an authored wrapper may own it
        // instead. Never assume parentElement is the scrolling surface.
        for (let node = table; node && root.contains(node); node = node.parentElement) {
          targets.add(node);
          if (node.clientWidth > 0 && node.scrollWidth > node.clientWidth + 1 &&
              /^(auto|scroll)$/.test(getComputedStyle(node).overflowX)) overflow.add(node);
          if (node === root) break;
        }
        // Inner table width can change while its constrained block stays the
        // same width (fonts, cells or a newly opened disclosure).
        table.querySelectorAll('thead, tbody, tfoot').forEach((node) => targets.add(node));
      });

      owned.forEach((element) => {
        if (element.getAttribute('tabindex') !== '0') owned.delete(element);
        else if (!overflow.has(element) && document.activeElement !== element) forget(element);
      });
      marked.forEach((element) => {
        if (!overflow.has(element) && document.activeElement !== element) unmark(element);
      });
      overflow.forEach((element) => {
        if (!element.hasAttribute('data-jp-rte-scroll')) {
          element.setAttribute('data-jp-rte-scroll', '');
          marked.add(element);
        }
        if (!element.hasAttribute('tabindex')) {
          element.setAttribute('tabindex', '0');
          owned.add(element);
        }
      });
      if (resize) {
        observed.forEach((element) => {
          if (!targets.has(element)) { resize.unobserve(element); observed.delete(element); }
        });
        targets.forEach((element) => {
          if (!observed.has(element)) { resize.observe(element); observed.add(element); }
        });
      }
    }

    controllers.set(root, { schedule, destroy });
    mutations.observe(root, { childList: true, subtree: true, characterData: true,
      attributes: true, attributeFilter: ['class', 'style', 'hidden', 'tabindex'] });
    root.addEventListener('focusout', schedule);
    root.addEventListener('load', schedule, true);
    schedule();
  };

  const init = (scope = document) => {
    const roots = [...scope.querySelectorAll(selector)];
    if (scope.matches?.(selector)) roots.unshift(scope);
    roots.forEach((root) => {
      if (!root.parentElement?.closest(selector) && !controllers.has(root)) create(root);
    });
  };
  const refresh = () => controllers.forEach(({ schedule }) => schedule());
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  window.addEventListener('resize', refresh, { passive: true });
  document.fonts?.addEventListener('loadingdone', refresh);
  document.addEventListener('shopify:section:load', (event) => { init(event.target); refresh(); });
  document.addEventListener('shopify:section:unload', (event) => {
    controllers.forEach(({ destroy }, root) => { if (event.target.contains(root)) destroy(); });
  });
})();
